// DropDownFilter.tsx
import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";

export interface Option {
  label: string;
  value: string;
  disabled?: boolean;
}

interface DropDownFilterProps {
  placeholder?: string;
  options: Option[];
  value: string[];                 // selected labels (like RewardsTable)
  onChange: (next: string[]) => void;
  maxMenuHeight?: number;
  disabled?: boolean;
}

/** Tiny arrow that rotates like in RewardsTable */
const CustomArrow: React.FC<{ open: boolean; onClick: () => void }> = ({
  open,
  onClick,
}) => (
  <span
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    style={{
      display: "inline-block",
      width: 11,
      height: 8,
      borderRight: "2px solid #000",
      borderBottom: "2px solid #000",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transition: "transform 0.2s ease",
      marginLeft: 20,
      marginRight: 10,
      position: "relative",
      right: 10,
      cursor: "pointer",
      pointerEvents: "auto",
    }}
  />
);

const DropDownFilter: React.FC<DropDownFilterProps> = ({
  placeholder = "Select Columns",
  options,
  value,
  onChange,
  maxMenuHeight = 300,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Work in "labels" end-to-end to mirror RewardsTable usage
  const allLabels = useMemo(() => options.map((o) => o.label), [options]);

  const filteredLabels = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allLabels;
    return allLabels.filter((lbl) => lbl.toLowerCase().includes(q));
  }, [allLabels, search]);

  const allFilteredSelected =
    filteredLabels.length > 0 &&
    filteredLabels.every((lbl) => value.includes(lbl));

  const toggleSelectAllOnFiltered = () => {
    if (allFilteredSelected) {
      onChange(value.filter((v) => !filteredLabels.includes(v)));
    } else {
      onChange(Array.from(new Set([...value, ...filteredLabels])));
    }
  };

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const raw = event.target.value as string[];
    // If user clicked the special __ALL__ item, toggle by filtered set
    if (raw.includes("__ALL__")) {
      toggleSelectAllOnFiltered();
      return;
    }
    onChange(raw);
  };

  return (
    <Tooltip
      title={value.length ? value.join(", ") : placeholder}
      placement="top"
      arrow
      PopperProps={{
        modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
      }}
    >
      <FormControl fullWidth disabled={disabled} sx={{ minWidth: 240, width: 240 }}>
        <Select
          multiple
          displayEmpty
          value={value}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={handleChange}
          input={
            <OutlinedInput
              sx={{
                height: 40,
                borderRadius: "10px",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  py: 0,
                },
              }}
            />
          }
          renderValue={(selected) =>
            (selected as string[]).length ? (
              (selected as string[]).join(", ")
            ) : (
              <Typography sx={{ color: "text.secondary" }}>{placeholder}</Typography>
            )
          }
          IconComponent={() => (
            <CustomArrow open={open} onClick={() => setOpen((v) => !v)} />
          )}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: maxMenuHeight,
                minWidth: 240,
                paddingTop: 0,
                paddingBottom: 0,
                borderRadius: 10,
                background: "#fff",
                boxShadow: "none",
                overflow: "overlay",
              },
            },
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
            disableAutoFocusItem: true,
            MenuListProps: {
              style: {
                paddingTop: 0,
                paddingBottom: 0,
                maxHeight: "250px",
                overflowY: "auto",
              },
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                  setTimeout(() => inputRef.current?.focus(), 0);
                }
              },
            },
          }}
        >
          {/* Search header like RewardsTable */}
          <ListSubheader
            sx={{
              bgcolor: "white",
              p: 1,
              display: "flex",
              alignItems: "center",
              position: "sticky",
              top: 0,
              zIndex: 10,
              borderBottom: "1px solid #eee",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                px: 1,
                border: "1px solid #ccc",
                borderRadius: 1,
                bgcolor: "transparent",
              }}
              onClick={(ev) => ev.stopPropagation()}
            >
              <input
                ref={inputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search columns..."
                style={{
                  width: "100%",
                  border: "none",
                  outline: "none",
                  fontSize: 14,
                  background: "transparent",
                  padding: "4px",
                  height: "36px",
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                }}
              />
              {search && (
                <Box
                  component="span"
                  sx={{ cursor: "pointer", fontWeight: "bold", fontSize: 18, px: 1 }}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setSearch("");
                    setTimeout(() => inputRef.current?.focus(), 0);
                  }}
                >
                  ×
                </Box>
              )}
            </Box>
          </ListSubheader>

          {/* Select All (on currently filtered items) */}
          <MenuItem
            dense
            value="__ALL__"
            onClick={(ev) => {
              ev.stopPropagation();
              toggleSelectAllOnFiltered();
            }}
          >
            <Checkbox checked={allFilteredSelected} />
            <ListItemText primary="Select All" />
          </MenuItem>

          {/* Options (labels) */}
          {filteredLabels.length ? (
            filteredLabels.map((label) => (
              <MenuItem key={label} value={label}>
                <Checkbox checked={value.includes(label)} />
                <ListItemText primary={label} />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled dense>
              <ListItemText primary="No results" />
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </Tooltip>
  );
};

export default DropDownFilter;
